import {
  createTheme,
  PaletteColor,
  PaletteColorOptions,
} from "@mui/material/styles";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    outlinedDark: true;
  }
}
declare module "@mui/material/Button" {
  interface ButtonPropsSizeOverrides {
    large: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    light: PaletteColor;
    dark: PaletteColor;
  }

  interface PaletteOptions {
    light?: PaletteColorOptions;
    dark?: PaletteColorOptions;
  }
}

const yesevaFont = "Yeseva One, sans-serif";
const montserratFont = "Montserrat, sans-serif";

/** Brand tokens: black foundation, deep red primary, gold as the premium accent. */
export const brand = {
  /** Chilli red: the dominant brand colour, not a trim. */
  primary: "#D0342C",
  primaryHover: "#E04B3C",
  primaryActive: "#A82419",
  primaryDark: "#6E1610",
  primarySoft: "#3A0D0A",
  /** Borders/rings that must stay visible on the red surfaces. */
  primaryBright: "#FF7A66",
  primaryVivid: "#E04B3C",
  primaryMuted: "#8E1D14",
  gold: "#D4AF37",
  goldHover: "#F0C94D",
  goldBright: "#F0C94D",
  goldHighlight: "#FFE184",
  goldSoft: "#B9943E",
  goldDark: "#9C781F",
  success: "#43C59E",
  warning: "#E7B84B",
  error: "#FF6B5E",
  info: "#5AA7E8",
};

export const lightBrand = {
  primary: "#9E1B1B",
  primaryHover: "#7F1414",
  primaryActive: "#7F1414",
  primaryDark: "#7F1414",
  primarySoft: "#F4E7C5",
  primaryBright: "#B88A35",
  primaryVivid: "#9E1B1B",
  primaryMuted: "#7F1414",
  gold: "#B88A35",
  goldHover: "#B88A35",
  goldBright: "#B88A35",
  goldHighlight: "#F4E7C5",
  goldSoft: "#B88A35",
  goldDark: "#946200",
  success: "#26734D",
  warning: "#946200",
  error: "#B42318",
  info: "#245B8F",
};

/** Page canvas: a smooth dark red-black ramp, never pure black. */
export const canvas = {
  top: "#210B0C",
  upper: "#2C1012",
  mid: "#280E10",
  lower: "#331214",
  bottom: "#1C0A0A",
};

/** Brand washes: red atmosphere and gold trim, used everywhere. */
export const glow = {
  red: "rgba(208, 52, 44, 0.42)",
  redSoft: "rgba(208, 52, 44, 0.22)",
  gold: "rgba(212, 175, 55, 0.24)",
};

export const lightGlow = {
  red: "rgba(158, 27, 27, 0.16)",
  redSoft: "rgba(158, 27, 27, 0.08)",
  gold: "rgba(184, 138, 53, 0.18)",
};

/** The one brand gradient: buttons, active tabs, selected surfaces. */
export const brandGradient = `linear-gradient(90deg, ${brand.primaryActive} 0%, ${brand.primaryHover} 50%, ${brand.primary} 100%)`;

export const palette = {
  dark: {
    page: "#2B0B09",
    deep: "#380D0A",
    bar: "#380D0A",
    panel: "#4A100C",
    panelSecondary: "#5C1410",
    field: "#340C09",
    rail: "#451009",
    border: "rgba(255, 122, 102, 0.30)",
    borderAccent: "rgba(212, 175, 55, 0.34)",
    textPrimary: "#F8F3EE",
    textSecondary: "#E8C9C4",
    textDisabled: "#C2A09B",
  },
  light: {
    page: "#F7F3EC",
    deep: "#F2EBDD",
    bar: "#FFFDF9",
    panel: "#FFFDF9",
    panelSecondary: "#F2EBDD",
    field: "#FFFDF9",
    rail: "#F2EBDD",
    border: "#E5D8C5",
    borderAccent: "#B88A35",
    textPrimary: "#2D211A",
    textSecondary: "#6F6257",
    textDisabled: "#927F70",
  },
};

// Function to create a theme based on the mode
const getTheme = (mode: "light" | "dark") => {
  const tone = palette[mode] ?? palette.dark;
  const accent = mode === "light" ? lightBrand : brand;
  const washes = mode === "light" ? lightGlow : glow;
  const primarySurface = mode === "light" ? accent.primary : brandGradient;
  const primaryHoverSurface = mode === "light" ? accent.primaryHover : brandGradient;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: accent.primary,
        light: accent.primaryHover,
        dark: accent.primaryDark,
        contrastText: "#FFFFFF",
      },
      background: {
        default: tone.page,
        paper: tone.panel,
      },
      text: {
        primary: tone.textPrimary,
        secondary: tone.textSecondary,
        disabled: tone.textDisabled,
      },
      secondary: {
        main: tone.textSecondary,
        light: accent.gold,
        dark: tone.textDisabled,
        contrastText: "#FFFFFF", // White text on secondary color
      },
      light: {
        main: palette.light.page,
        light: mode === "light" ? palette.light.panel : "#FFFFFF",
        dark: palette.light.deep,
      },
      dark: {
        main: palette.dark.page,
        light: palette.dark.panel,
        dark: palette.dark.field,
      },
      error: {
        main: accent.error,
        light: "#E67A7A",
        dark: "#B03A3A",
      },
      warning: {
        main: accent.warning,
        light: "#F0CE7C",
        dark: "#B98F33",
      },
      info: {
        main: accent.info,
        light: "#8CC3F0",
        dark: "#3D7DB5",
      },
      success: {
        main: accent.success,
        light: "#7BD9BE",
        dark: "#2F9C7B",
      },
    },
    typography: {
      fontFamily: montserratFont,
      h1: { fontFamily: yesevaFont },
      h2: { fontFamily: yesevaFont },
      h3: { fontFamily: yesevaFont },
      h4: { fontFamily: yesevaFont },
      h5: { fontFamily: yesevaFont },
      h6: { fontFamily: yesevaFont },
      subtitle1: { fontFamily: yesevaFont },
      button: { fontFamily: montserratFont },
    },
    components: {
      MuiPaper: {
        defaultProps: {
          elevation: 2,
        },
        styleOverrides: {
          root: {
            // padding: "20px",
            backgroundColor: tone.panel,
            backgroundImage: "none",
            "&.table-paper": {
              boxShadow: "none",
              borderRadius: 0,
            },
          },
          rounded: {
            borderRadius: "20px",
          },
          outlined: {
            borderColor: tone.borderAccent,
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            border: `1px solid ${tone.border}`,
            borderRadius: "4px",
            background: tone.rail,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: tone.textPrimary,
            border: "none",
            "&.Mui-selected": {
              background: primarySurface,
              color: "#FFFFFF",
            },
            "&:hover": {
              backgroundColor: washes.redSoft,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: "contained",
        },
        styleOverrides: {
          root: {
            borderRadius: "12px",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          },
          sizeLarge: {
            padding: "16px 24px",
            fontSize: "18px",
            width: "100%", // full width
          },
          sizeMedium: {
            padding: "10px 20px",
          },
          sizeSmall: {
            padding: "6px 16px",
            fontSize: "14px",
          },
        },
        variants: [
          {
            props: { variant: "contained" },
            style: {
              background: primarySurface,
              color: "#fff",
              border: `1px solid ${mode === "light" ? accent.gold : `${brand.gold}73`}`,
              boxShadow: `0 8px 24px ${washes.red}`,
              "&:hover": {
                background: primaryHoverSurface,
                filter: mode === "light" ? "none" : "brightness(1.14)",
                borderColor: mode === "light" ? accent.gold : `${brand.gold}A6`,
                boxShadow: `0 12px 30px ${washes.red}, 0 0 18px ${washes.gold}`,
              },
              "&.Mui-disabled": {
                background: mode === "light" ? palette.light.deep : glow.red,
                color: mode === "light" ? palette.light.textSecondary : "rgba(248, 243, 236, 0.45)",
              },
            },
          },
          {
            props: { variant: "outlinedDark" },
            style: {
              background: mode === "light" ? tone.panel : `linear-gradient(180deg, ${tone.panel} 0%, ${brand.primarySoft} 100%)`,
              border: `1px solid ${tone.border}`,
              boxShadow: "none",
              color: tone.textPrimary,
              fontWeight: "bold",
              padding: "10px 24px",
              "&:hover": {
                background: mode === "light" ? tone.panelSecondary : `linear-gradient(180deg, ${brand.primarySoft} 0%, ${brand.primaryDark} 100%)`,
                border: `1px solid ${mode === "light" ? accent.gold : `${brand.gold}73`}`,
              },
            },
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tone.panel,
            padding: "24px",
            borderRadius: "16px",
            height: "100%",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: tone.textPrimary,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: tone.field,
            borderRadius: "8px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: tone.border,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: tone.borderAccent,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: mode === "light" ? accent.gold : brand.primaryBright,
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px rgba(99, 25, 26, 0.28)`,
            },
            "& .MuiSelect-select": {
              color: tone.textPrimary,
              fontSize: "14px",
            },
            "& .MuiSvgIcon-root": {
              color: tone.textSecondary,
            },
            "& .MuiPaper-root": {
              backgroundColor: `${tone.field} !important`,
              borderRadius: "0 0 4px 4px !important",

              "& .MuiMenuItem-root": {
                color: tone.textPrimary,
                fontSize: "14px",
                backgroundColor: tone.field,
                "&:hover": {
                  backgroundColor: `${washes.redSoft} !important`,
                },
                "&.Mui-selected": {
                  backgroundColor: `${washes.red} !important`,
                  "&:hover": {
                    backgroundColor: "rgba(99, 25, 26, 0.52) !important",
                  },
                },
              },
            },
            "& .MuiPopover-paper": {
              backgroundColor: `${tone.field} !important`,
              borderRadius: "0 0 4px 4px !important",
            },
            "& .MuiMenu-paper": {
              backgroundColor: `${tone.field} !important`,
              borderRadius: "0 0 4px 4px !important",
            },
          },
        },
      },
    },
  });
};

export default getTheme;
